import { useEffect, useState } from 'react'
import {
  getPushState,
  sendTestPush,
  subscribeToPush,
  unsubscribeFromPush,
  type PushState,
} from '../lib/push.ts'

/**
 * Настройка уведомлений.
 *
 * Отдельный экран нужен ровно из-за iPhone: там уведомления не работают,
 * пока приложение не добавлено на экран «Домой», и человеку надо объяснить
 * это словами. Показать кнопку, которая молча ничего не делает, — худший
 * вариант: решат, что сломано.
 */
export function NotificationSettings({ onClose }: { onClose: () => void }) {
  const [state, setState] = useState<PushState | null>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const refresh = () => {
    getPushState()
      .then(setState)
      .catch(() => setState({ kind: 'unsupported', reason: 'Не удалось проверить' }))
  }

  useEffect(refresh, [])

  const act = async (fn: () => Promise<void>, done: string) => {
    setBusy(true)
    setError(null)
    setMessage(null)
    try {
      await fn()
      setMessage(done)
      refresh()
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Не получилось')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sheet" onClick={onClose}>
      <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2>Уведомления</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        {!state && (
          <div className="center-screen" style={{ height: 120 }}>
            <div className="spinner" />
          </div>
        )}

        {state?.kind === 'needs-install' && (
          <>
            <p className="sheet-text">
              На iPhone и iPad уведомления приходят только в установленном приложении.
              Из обычной вкладки Safari они не работают — это ограничение системы,
              обойти его нельзя.
            </p>
            <ol className="sheet-steps">
              <li>Нажми кнопку «Поделиться» внизу экрана — квадрат со стрелкой вверх</li>
              <li>Пролистай список и выбери «На экран „Домой“»</li>
              <li>Нажми «Добавить»</li>
              <li>Открой «Семью» с домашнего экрана и вернись сюда</li>
            </ol>
            <p className="sheet-note">Нужна iOS 16.4 или новее.</p>
          </>
        )}

        {state?.kind === 'unsupported' && (
          <p className="sheet-text">{state.reason}. Сообщения всё равно будут приходить,
            пока приложение открыто.</p>
        )}

        {state?.kind === 'not-configured' && (
          <p className="sheet-text">Уведомления на сервере пока не настроены.</p>
        )}

        {state?.kind === 'denied' && (
          <>
            <p className="sheet-text">
              Уведомления запрещены для этого сайта. Снять запрет можно только
              в настройках браузера — из приложения это сделать нельзя.
            </p>
            <p className="sheet-note">
              На iPhone: «Настройки» → «Уведомления» → «Семья» → «Допуск уведомлений».
              На компьютере: значок замка рядом с адресом → «Уведомления» → «Разрешить».
            </p>
          </>
        )}

        {state?.kind === 'available' && (
          <>
            <p className="sheet-text">
              Сообщения будут приходить, даже когда приложение закрыто.
              Уведомление не придёт, если ты уже смотришь в эту переписку.
            </p>
            <button
              className="btn-primary"
              disabled={busy}
              onClick={() => void act(subscribeToPush, 'Готово. Проверь отправкой пробного.')}
            >
              {busy ? '…' : 'Включить уведомления'}
            </button>
          </>
        )}

        {state?.kind === 'subscribed' && (
          <>
            <p className="sheet-text">Уведомления включены на этом устройстве.</p>
            <div className="sheet-actions">
              <button
                className="btn-primary"
                disabled={busy}
                onClick={() =>
                  void act(async () => {
                    const { delivered } = await sendTestPush()
                    if (delivered === 0) throw new Error('Ни одно устройство не откликнулось')
                  }, 'Отправил. Уведомление должно появиться через пару секунд.')
                }
              >
                {busy ? '…' : 'Отправить пробное'}
              </button>
              <button
                className="btn-quiet"
                disabled={busy}
                onClick={() => void act(unsubscribeFromPush, 'Уведомления выключены.')}
              >
                Выключить
              </button>
            </div>
          </>
        )}

        {message && <div className="sheet-ok">{message}</div>}
        {error && <div className="sheet-error">{error}</div>}
      </div>
    </div>
  )
}
